import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { myProfile } from 'Redux/user-profile/selectors';
import { withCapacitor } from 'Util/Capacitors/hoc';
import { useMyCourseData } from 'Domain/MyCourseData';
import { useGetFeedbackTemplate, useGiveFeedback } from 'Util/FeedBackService';
import { FeedbackImprovementAndSatisfiedModal, OPTIONS_STATE } from 'Components/Feedback';
import { useClassroomContext } from 'Context/ClassroomContext';

import { REPORT_STATE } from './constant';

function getFormOptions(options) {
  if (!options) return [];
  return options.map((option) => ({ label: option.title, value: option.value }));
}

function getFormRating(min = 0, max = 10) {
  const ratings = [];
  for (let i = min; i <= max; i++) ratings.push(i);
  return ratings;
}

const FeedbackImprovementAndSatisfied = ({
  capacitorContext: { deviceInfo },
  user,
  contentType,
  isShow,
  progressToShow,
  currentNPSState,
  handleSubmitFeedback,
  handleCancelFeedback,
}) => {
  const { t } = useTranslation('feedback');
  const { data: templateData, loading: isTemplateLoading } = useGetFeedbackTemplate({
    templateId: 'IMPROVEMENT_SATISFIED_CONTENT_FEEDBACK',
  });

  const { courseCode } = useMyCourseData();
  const { currentSubsectionId } = useClassroomContext();

  const [reportState, setReportState] = useState(REPORT_STATE.INITIALIZED);

  const { giveFeedback, loading: isGiveFeedbackLoading } = useGiveFeedback({
    templateId: 'IMPROVEMENT_SATISFIED_CONTENT_FEEDBACK',
    onCompleted: () => {
      setReportState(REPORT_STATE.COMPLETED);
    },
  });

  const templateComponents = templateData?.getTemplate?.components ?? [];
  const inputComponents = templateComponents.reduce(
    (resultObj, item) => Object.assign(resultObj, { [item.name]: item }),
    {}
  );

  const handleCloseModal = () => {
    if (reportState === REPORT_STATE.COMPLETED) {
      handleSubmitFeedback();
    } else {
      handleCancelFeedback();
    }
  };

  return (
    <FeedbackImprovementAndSatisfiedModal
      onCloseModal={handleCloseModal}
      formId="feedback-improvement-and-satisfied-content-form"
      isOpen={isShow && !isTemplateLoading}
      loading={isGiveFeedbackLoading}
      title={t('IMPROVEMENT_AND_SATISFIED.CONTENT.TITLE')}
      ratings={getFormRating(
        inputComponents['rating']?.minValue,
        inputComponents['rating']?.maxValue
      )}
      satisfiedLabel={t('IMPROVEMENT_AND_SATISFIED.CONTENT.OPTION_SATISFIED_LABEL')}
      satisfiedOptions={getFormOptions(inputComponents['satisfiedCategory']?.options)}
      improvementLabel={t('IMPROVEMENT_AND_SATISFIED.CONTENT.OPTION_IMPROVEMENT_LABEL')}
      improvementOptions={getFormOptions(inputComponents['improvementCategory']?.options)}
      getOptionState={(rating) =>
        isNil(rating)
          ? OPTIONS_STATE.DISABLED
          : rating > 6
          ? OPTIONS_STATE.SATISFIED
          : OPTIONS_STATE.IMPROVEMENT
      }
      isSuccess={reportState === REPORT_STATE.COMPLETED}
      onSubmit={(formValue) => {
        giveFeedback(
          courseCode,
          user.id,
          {
            firstname: user.firstname,
            lastname: user.lastname,
            subsectionId: currentSubsectionId,
            progress:
              Number.isInteger(currentNPSState) && progressToShow.length > currentNPSState
                ? progressToShow[currentNPSState]
                : undefined,
            ...deviceInfo,
          },
          contentType,
          {
            feedback: formValue.feedback ?? '',
            rating: formValue.rating,
            satisfiedCategory: formValue.satisfiedCategory ?? [],
            improvementCategory: formValue.improvementCategory ?? [],
          }
        );
      }}
    />
  );
};

FeedbackImprovementAndSatisfied.propTypes = {
  isShow: PropTypes.bool,
  contentType: PropTypes.string,
  currentNPSState: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  progressToShow: PropTypes.array,
  handleSubmitFeedback: PropTypes.func,
  handleCancelFeedback: PropTypes.func,
  user: PropTypes.object,
  capacitorContext: PropTypes.object,
};

FeedbackImprovementAndSatisfied.defaultProps = {
  contentType: 'NPS',
  progressToShow: [],
  handleSubmitFeedback: () => {},
  handleCancelFeedback: () => {},
};

export default compose(
  withCapacitor,
  connect((state) => ({ user: myProfile(state) }), {})
)(FeedbackImprovementAndSatisfied);
